/**
 * JobIdentityBlockDialog — shown when the server refuses to hand an existing job
 * to a different client (409 `job_identity_locked`).
 *
 * This is the UI half of the Valle Verde fix. Before it existed, saving a quote
 * for a new client against a linked job silently overwrote that job's client,
 * title, dates and revenue — the job vanished from the board and a paid deposit
 * ended up attached to someone else's work. The server now blocks that; this
 * explains why, offers a separate job, and lets an admin confirm a client that
 * really changed.
 */
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, Alert, List, ListItem, ListItemIcon, ListItemText, Chip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockIcon from '@mui/icons-material/Lock';
import { useMobile } from '../../hooks/useMobile';

/**
 * @param {object}   block             the 409 body
 * @param {function} [onCreateSeparate] offered where a new job is the right answer
 *   (the quote builder). Omitted on Job Detail, which is editing one job in place.
 * @param {function} [onOverride]      "Change client" — keep this job and hand it
 *   to the new client: the client really changed, or it is the same person under
 *   a duplicate CRM contact. Only rendered when the server says this user may
 *   override; the server logs it.
 */
export default function JobIdentityBlockDialog({
  block, onCreateSeparate, onOverride, onCancel, saving,
}) {
  const { isMobile } = useMobile();
  const current = block?.current || {};
  const incoming = block?.incoming || {};
  const reasons = block?.reasons || [];
  const canOverride = block?.canOverride === true && typeof onOverride === 'function';
  const hasInvoices = (block?.anchors?.invoices?.length || 0) > 0;

  return (
    <Dialog open onClose={saving ? undefined : onCancel} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <WarningAmberIcon color="warning" />
        <Typography variant="h6" component="span">This job belongs to someone else</Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Alert severity="warning" icon={<LockIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          Saving would move <strong>{current.title || 'this job'}</strong> from{' '}
          <strong>{current.client || 'its current client'}</strong> to{' '}
          <strong>{incoming.client || 'the new client'}</strong> — taking its quote,
          invoices and crew payouts with it.
        </Alert>

        {reasons.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              What is already committed to {current.client || 'this job'}:
            </Typography>
            <List dense disablePadding>
              {reasons.map((r, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <LockIcon fontSize="small" color="disabled" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={r} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: '0.875rem' }}>
          <Typography variant="body2" color="text.secondary">Job today</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>{current.title || '—'}</Typography>
            <Chip size="small" variant="outlined" label={current.client || 'no client'} />
          </Box>
          <Typography variant="body2" color="text.secondary">This quote</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>{incoming.title || '—'}</Typography>
            <Chip size="small" variant="outlined" color="primary" label={incoming.client || 'no client'} />
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
          {onCreateSeparate && (
            <Typography variant="body2" color="text.secondary">
              Different client, different job? <strong>Create a separate job</strong> — it keeps both intact.
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {canOverride ? (
              <>
                {onCreateSeparate
                  ? 'Same job, but the client changed?'
                  : 'The client changed, or it’s the same person under a duplicate CRM contact?'}
                {' '}<strong>Change client</strong> puts this job under {incoming.client || 'the new client'}.
                {hasInvoices && (current.contactId
                  ? ' Issued invoices stay on the job, still billed to their original client.'
                  : ' Issued invoices go with it to the new client.')}
                {' '}The change is logged.
              </>
            ) : 'If the client on this job really changed, ask an admin to change it.'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        disableSpacing={isMobile}
        sx={{ px: 3, py: 2, gap: 1, ...(isMobile && { flexDirection: 'column-reverse', alignItems: 'stretch' }) }}
      >
        <Button onClick={onCancel} disabled={saving} color="inherit">Cancel</Button>
        {canOverride && (
          <Button onClick={onOverride} disabled={saving} color="warning" variant="outlined">
            {saving ? 'Saving…' : 'Change client'}
          </Button>
        )}
        {onCreateSeparate && (
          <Button onClick={onCreateSeparate} disabled={saving} variant="contained">
            {saving ? 'Creating…' : 'Create a separate job'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
